import React, { useState, useEffect } from 'react';
import { FaBell, FaCheckDouble, FaNewspaper, FaCircle, FaCalendarAlt, FaInfoCircle, FaExclamationTriangle, FaDollarSign } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../../context/NotificationContext';
import { useAuth } from '../../../context/AuthContext';
import './Notification.css';

// Icon mapping for different notification types
const TYPE_ICONS = {
  news: FaNewspaper,
  payment: FaDollarSign,
  defaulter: FaExclamationTriangle,
  backlog: FaCalendarAlt,
  info: FaInfoCircle,
  system: FaBell
};

// Get news image URL helper
const getNewsImageUrl = (notification) => {
  if (notification.type !== 'news' || !notification.relatedId) return null;
  // You can extend this to fetch actual news images if needed
  return null;
};

const Notification = () => {
  const { notifications, unread, markRead, markAllRead, load } = useNotifications();
  const { user } = useAuth();
  const navigate  = useNavigate();
  const [loading, setLoading] = useState(true);

  const newsPath = user?.role === 'collegeadmin' ? '/collegeadmin/news/view'
    : user?.role === 'clerk'        ? '/clerk/news/view'
    : user?.role === 'superadmin'   ? '/superadmin/news/view'
    : '/admin/news/view';

  // Load notifications on component mount
  useEffect(() => {
    const loadNotifications = async () => {
      setLoading(true);
      await load();
      setLoading(false);
    };
    loadNotifications();
  }, [load]);

  // Format time helper
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} mins ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`;
    if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)} days ago`;
    
    return date.toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const handleNotifClick = async (notif) => {
    if (!notif.read) await markRead(notif._id);
    if (notif.type === 'news' && notif.relatedId) {
      navigate(newsPath, { state: { openNewsId: notif.relatedId } });
    }
  };

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      await markAllRead();
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  // Get icon for notification type
  const getTypeIcon = (type) => {
    const IconComponent = TYPE_ICONS[type] || FaInfoCircle;
    return <IconComponent />;
  };

  if (loading) {
    return (
      <div className="notification-container">
        <div className="notification-header">
          <h1 className="notification-page-title">
            <FaBell className="notification-icon-primary" />
            Notifications
          </h1>
        </div>
        <div className="notification-empty">
          <FaBell className="notification-empty-icon" />
          <h3>Loading notifications...</h3>
          <p>Please wait while we fetch your latest updates.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="notification-container">
      {/* Header Area */}
      <div className="notification-header">
        <h1 className="notification-page-title">
          <FaBell className="notification-icon-primary" />
          Notifications
          {unread > 0 && (
            <span className="notification-badge">{unread} New</span>
          )}
        </h1>
        
        {notifications.length > 0 && (
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {unread > 0 && (
              <button className="notification-mark-all-btn" onClick={handleMarkAllAsRead}>
                <FaCheckDouble /> Mark all as read
              </button>
            )}
          </div>
        )}
      </div>

      {/* Notifications List */}
      {notifications.length === 0 ? (
        <div className="notification-empty">
          <FaBell className="notification-empty-icon" />
          <h3>All Caught Up!</h3>
          <p>You have no new notifications right now.</p>
        </div>
      ) : (
        <div className="notification-list">
          {notifications.map((notif) => (
            <div 
              key={notif._id} 
              className={`notification-item ${!notif.read ? 'notification-unread' : ''} ${notif.type === 'news' ? 'notification-clickable' : ''}`}
              onClick={() => handleNotifClick(notif)}
            >
              {/* Unread Dot Indicator */}
              <div className="notification-indicator">
                {!notif.read && <FaCircle className="notification-dot" />}
              </div>

              {/* 16:9 Thumbnail Image */}
              <div className="notification-thumbnail">
                {getNewsImageUrl(notif) ? (
                  <img src={getNewsImageUrl(notif)} alt="Notification thumbnail" />
                ) : (
                  <div className="notification-placeholder">
                    {getTypeIcon(notif.type)}
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="notification-content">
                <div className="notification-content-header">
                  <h3 className="notification-title">{notif.title}</h3>
                  <span className="notification-time">{formatTime(notif.createdAt)}</span>
                </div>
                <p className="notification-message">{notif.message}</p>
              </div>

              {/* Actions - no delete button */}
              <div className="notification-actions">
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Notification;